import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { OarCacheService } from '../oar-cache-service';
import type { ClaimAnalysisResponse } from '../types/claim-analysis';

/**
 * 缓存项数据结构
 */
interface CacheItem<T> {
    data: T;
    expiry: number; // 过期时间戳（毫秒）
    timer?: NodeJS.Timeout; // 自动清理定时器
}

/**
 * OAR 缓存服务实现
 *
 * 基于 Map 的内存缓存，支持 TTL（生存时间）自动过期
 *
 * 特性：
 * - 15分钟默认过期时间
 * - 自动内存清理，防止内存泄漏
 * - 高效的缓存键查找
 * - 支持手动清除
 */
@injectable()
export class CachedOarCacheServiceImpl implements OarCacheService {
    private readonly _cache = new Map<string, CacheItem<ClaimAnalysisResponse>>();
    private readonly _defaultTtlMs = 15 * 60 * 1000; // 15分钟
    private readonly _cleanupIntervalMs = 60 * 1000; // 1分钟清理间隔
    private _cleanupTimer?: NodeJS.Timeout;

    constructor() {
        this._startPeriodicCleanup();
    }

    /**
     * 获取缓存数据
     *
     * @param cacheKey - 缓存键
     * @returns 缓存数据或 null（不存在或已过期）
     */
    get(cacheKey: string): ClaimAnalysisResponse | null {
        const item = this._cache.get(cacheKey);

        if (!item) {
            return null;
        }

        // 检查是否过期
        if (Date.now() > item.expiry) {
            this._deleteItem(cacheKey, item);
            return null;
        }

        return item.data;
    }

    /**
     * 设置缓存数据
     *
     * @param cacheKey - 缓存键
     * @param data - 缓存数据
     * @param ttlMs - 过期时间（毫秒），默认15分钟
     */
    set(cacheKey: string, data: ClaimAnalysisResponse, ttlMs: number = this._defaultTtlMs): void {
        // 清理现有的缓存项（如果存在）
        const existingItem = this._cache.get(cacheKey);
        if (existingItem) {
            this._deleteItem(cacheKey, existingItem);
        }

        // 计算过期时间
        const expiry = Date.now() + ttlMs;

        // 创建新的缓存项
        const item: CacheItem<ClaimAnalysisResponse> = {
            data,
            expiry,
        };

        // 设置自动清理定时器
        item.timer = setTimeout(() => {
            this._deleteItem(cacheKey, item);
        }, ttlMs);

        // 存储到缓存
        this._cache.set(cacheKey, item);
    }

    /**
     * 删除指定缓存项
     *
     * @param cacheKey - 缓存键
     */
    delete(cacheKey: string): void {
        const item = this._cache.get(cacheKey);
        if (item) {
            this._deleteItem(cacheKey, item);
        }
    }

    /**
     * 清空所有缓存
     */
    clear(): void {
        // 清理所有定时器
        for (const [key, item] of this._cache.entries()) {
            this._deleteItem(key, item);
        }

        this._cache.clear();
    }

    /**
     * 检查缓存项是否存在且未过期
     *
     * @param cacheKey - 缓存键
     * @returns 是否存在有效缓存
     */
    has(cacheKey: string): boolean {
        const item = this._cache.get(cacheKey);

        if (!item) {
            return false;
        }

        // 检查是否过期
        if (Date.now() > item.expiry) {
            this._deleteItem(cacheKey, item);
            return false;
        }

        return true;
    }

    /**
     * 获取缓存项数量
     *
     * @returns 当前有效缓存项数量
     */
    size(): number {
        // 先清理过期项，然后返回剩余数量
        this._cleanupExpiredItems();
        return this._cache.size;
    }

    /**
     * 启动定期清理任务
     *
     * @private
     */
    private _startPeriodicCleanup(): void {
        this._cleanupTimer = setInterval(() => {
            this._cleanupExpiredItems();
        }, this._cleanupIntervalMs);
    }

    /**
     * 清理过期的缓存项
     *
     * @private
     */
    private _cleanupExpiredItems(): void {
        const now = Date.now();
        const expiredKeys: string[] = [];

        for (const [key, item] of this._cache.entries()) {
            if (now > item.expiry) {
                expiredKeys.push(key);
            }
        }

        for (const key of expiredKeys) {
            const item = this._cache.get(key);
            if (item) {
                this._deleteItem(key, item);
            }
        }
    }

    /**
     * 删除缓存项并清理相关资源
     *
     * @param cacheKey - 缓存键
     * @param item - 缓存项
     * @private
     */
    private _deleteItem(cacheKey: string, item: CacheItem<ClaimAnalysisResponse>): void {
        // 清理定时器
        if (item.timer) {
            clearTimeout(item.timer);
            item.timer = undefined;
        }

        // 从缓存中删除
        this._cache.delete(cacheKey);
    }

    /**
     * 销毁缓存服务，清理所有资源
     *
     * 通常在应用关闭时调用
     */
    dispose(): void {
        // 清理定期清理定时器
        if (this._cleanupTimer) {
            clearInterval(this._cleanupTimer);
            this._cleanupTimer = undefined;
        }

        // 清理所有缓存项
        this.clear();
    }
}
